import {
  ANCILLARY_BUNDLE,
  checkInBaggage,
  MutuallyExclusive as MutuallyExclusiveComponent,
  mutuallyExclusive,
  secondBaggagePerTraveler,
  TRAVEL_INSURANCE_INSIDE_EU,
  TRAVEL_INSURANCE_OUTSIDE_EU,
} from '@eti/products';
import type {
  AncillaryBundlesOnTdPageFragment,
  ExtraProductTexts,
  GetSelectedTripOnTravelerDetailsPageQuery,
  MutuallyExclusive,
} from '@eti/schema-types';
import { EventSegmentType, ProductPreSelection } from '@eti/schema-types';
import type { ReactNode } from 'react';
import uniqBy from 'lodash/uniqBy';
import { TRAVELER_DETAILS } from '../../../constants/formNamesConstants';
import type {
  AvailableProductType,
  GenerateProductComponentReturnFunctionReturnType,
  GetOrderReturnType,
  GetProductsFromReturnType,
  ProductInputType,
  StructuredExtraComponentType,
  SupportedProductsType,
  TransformedBounds,
  TransformedTravelersDataType,
  TravelerDetailsQueryMutuallyExclusiveProductProductTypesType,
} from '../../../common/utils/models';
import type {
  TravelerDetailsQueryBoundType,
  TravelerDetailsQueryIncludedExtraProductType,
} from '../../../common/types/queries/getSelectedTripOnTravelerDetailsPageQuery/models';
import type { AvailableExtraProductsOnTdPageFragmentSellSpecificationType } from '../../../common/types/fragments/models';
import type {
  TransformedBundleDataOption,
  TransformedBundleDataOptionFeatureType,
} from '../../../common/types/transformations/models';

export const getAncillaryBundleProduct = (
  ancillaryBundleId: string,
  availableBundles: TransformedBundleDataOption[] = [],
  discountPercentage: number | null | undefined,
  isAncillaryBundlingEnabled: boolean,
  travelers: TransformedTravelersDataType,
) => {
  if (!(availableBundles.length > 0 && isAncillaryBundlingEnabled)) {
    return null;
  }
  const ancillaryBundleProductsIds =
    availableBundles.length === 0
      ? availableBundles
      : uniqBy(
          availableBundles.reduce(
            (
              acc: TransformedBundleDataOptionFeatureType[],
              bundle: TransformedBundleDataOption[][number],
            ) => {
              return bundle?.features ? [...acc, ...bundle?.features] : [...acc];
            },
            [],
          ),
          'name',
        ).map((product) => Number(product.productTypeId));

  const AncillaryBundleComponent = ANCILLARY_BUNDLE.component;

  return {
    __typename: availableBundles[0]?.__typename,
    id: ANCILLARY_BUNDLE.id,
    ids: ancillaryBundleProductsIds,
    code: 'ANCILLARY_BUNDLE',
    component: (
      <AncillaryBundleComponent
        ancillaryBundleId={ancillaryBundleId}
        discountPercentage={discountPercentage ?? 0}
        form={TRAVELER_DETAILS}
        isAncillaryBundlingEnabled={isAncillaryBundlingEnabled}
        isLoading={false}
        options={availableBundles}
        selectedValue={null}
        travelers={travelers}
      />
    ),
  };
};

export const isBaggageSelectedWithBundle = (
  availableBundles: AncillaryBundlesOnTdPageFragment[],
) => {
  if (availableBundles.length === 0) {
    return null;
  }
  const selectedBundle = availableBundles[0]?.options.find(
    (option) => option?.id === availableBundles[0]?.selectedOption,
  );
  const baggageInBundle = selectedBundle?.content?.find(
    (product) => product?.productTypeId === checkInBaggage.id.toString(),
  );
  if (baggageInBundle) {
    return {
      weight: baggageInBundle.weight ?? 0,
      weightUnit: baggageInBundle.weightUnit ?? '',
      pieces: baggageInBundle.pieces ?? 0,
    };
  }
  return null;
};

const getMutuallyExclusiveDataFromGroup = (
  purchasableExtraProducts: StructuredExtraComponentType[],
  productTypes: TravelerDetailsQueryMutuallyExclusiveProductProductTypesType,
) => {
  const presentMuxProducts: StructuredExtraComponentType[] = purchasableExtraProducts.filter(
    (structuredExtraProduct) => productTypes?.includes(Number(structuredExtraProduct.id)),
  );
  if (presentMuxProducts.length < 1) {
    return { component: null, productsToFilter: [] };
  }
  const purchasableMuxProducts = presentMuxProducts.filter(
    ({ selectedWithTrip }) => !selectedWithTrip,
  );

  const component =
    purchasableMuxProducts.length > 1 ? (
      <MutuallyExclusiveComponent form={TRAVELER_DETAILS} products={presentMuxProducts} />
    ) : null;
  const productsToFilter = purchasableMuxProducts.map(({ id }) => Number(id));

  return {
    component: {
      component,
      code: mutuallyExclusive.formSection,
      id: mutuallyExclusive.id,
    },
    productsToFilter,
  };
};

export const transformMutuallyExclusiveData = (
  purchasableExtraProducts: StructuredExtraComponentType[],
  mutuallyExclusiveProducts: (MutuallyExclusive | null | undefined)[],
  isMutuallyExclusiveEnabled: boolean,
) => {
  if (!isMutuallyExclusiveEnabled) {
    return { components: [], productsToFilter: [] };
  }
  return mutuallyExclusiveProducts?.reduce(
    (acc, product) => {
      const { component, productsToFilter } = getMutuallyExclusiveDataFromGroup(
        purchasableExtraProducts,
        product?.productTypes,
      );
      if (component) {
        acc.components.push(component);
        acc.productsToFilter.push(...productsToFilter);
      }
      return acc;
    },
    {
      components: [] as ({
        component: any | null;
        code: string;
        id: string;
      } | null)[],
      productsToFilter: [] as number[],
    },
  );
};

export const getDBFProductsFrom = (
  supportedProducts: SupportedProductsType,
  availableProducts: (
    | StructuredExtraComponentType
    | SupportedProductsType[number]
    | TravelerDetailsQueryIncludedExtraProductType
  )[] = [],
): GetProductsFromReturnType[] =>
  Object.values(availableProducts).reduce(
    (acc: GetProductsFromReturnType[], curr: NonNullable<AvailableProductType>) => {
      const product: SupportedProductsType[number] | undefined = supportedProducts.find(
        (supportedProduct) => Number(curr.id) === supportedProduct?.id,
      );

      if (!product) {
        return acc;
      }

      const __typename =
        'sellSpecification' in curr && curr.sellSpecification.__typename
          ? curr.sellSpecification.__typename
          : '';
      const persuasion = 'persuasion' in curr ? curr.persuasion : { isEnabled: false };
      const selectedWithTrip = 'selectedWithTrip' in curr ? curr.selectedWithTrip : false;
      const sellSpecification = 'sellSpecification' in curr ? curr.sellSpecification : {};
      const shouldShowPricePerBound =
        'shouldShowPricePerBound' in curr ? curr.shouldShowPricePerBound : false;
      const productPreSelection =
        'productPreSelection' in curr && curr.productPreSelection
          ? curr.productPreSelection
          : ProductPreSelection.None;
      const code = 'code' in curr ? curr.code : '';
      const texts = 'texts' in curr ? curr.texts : undefined;

      return [
        ...acc,
        {
          ...product,
          __typename,
          persuasion,
          productIncludedTexts: texts,
          sellSpecification,
          selectedWithTrip,
          shouldShowPricePerBound,
          productPreSelection,
          code,
        },
      ];
    },
    [],
  );

export const getProductsFrom = (
  supportedProducts: SupportedProductsType | SupportedProductsType[number],
  availableProducts: (
    | StructuredExtraComponentType
    | SupportedProductsType[number]
    | TravelerDetailsQueryIncludedExtraProductType
  )[] = [],
): GetProductsFromReturnType[] =>
  Object.values(supportedProducts).reduce(
    (acc: GetProductsFromReturnType[], curr: SupportedProductsType[number]) => {
      const product: AvailableProductType | undefined = availableProducts.find(
        (availableProduct: AvailableProductType) =>
          curr.id ===
          Number(
            typeof availableProduct === 'object' && availableProduct && 'id' in availableProduct
              ? availableProduct?.id
              : '',
          ),
      );

      if (!product) {
        return acc;
      }

      const __typename =
        typeof product === 'object' &&
        'sellSpecification' in product &&
        product?.sellSpecification?.__typename
          ? product?.sellSpecification?.__typename
          : '';
      const persuasion =
        typeof product === 'object' && 'persuasion' in product
          ? product.persuasion
          : { isEnabled: false };
      const selectedWithTrip =
        typeof product === 'object' && 'selectedWithTrip' in product
          ? product.selectedWithTrip
          : false;
      const sellSpecification =
        typeof product === 'object' && 'sellSpecification' in product
          ? product.sellSpecification
          : {};
      const shouldShowPricePerBound =
        typeof product === 'object' && 'shouldShowPricePerBound' in product
          ? product.shouldShowPricePerBound
          : false;
      const productPreSelection =
        typeof product === 'object' &&
        'productPreSelection' in product &&
        product.productPreSelection
          ? product.productPreSelection
          : ProductPreSelection.None;
      const code = typeof product === 'object' && 'code' in product ? product.code : '';
      const texts = typeof product === 'object' && 'texts' in product ? product.texts : {};

      return [
        ...acc,
        {
          ...curr,
          __typename,
          persuasion,
          productIncludedTexts: texts,
          sellSpecification,
          selectedWithTrip,
          shouldShowPricePerBound,
          productPreSelection,
          code,
        },
      ];
    },
    [],
  );

export const getEventSegmentsList = (bounds: TravelerDetailsQueryBoundType[] = []) => {
  const segments = bounds.flatMap((bound) => bound?.segments || []);

  return segments
    .filter((segment) => {
      return segment?.__typename === 'EventSegment';
    })
    .map((segment) => (segment && 'types' in segment ? segment?.types : []));
};

const transformEventSegmentTypes = (
  types: (EventSegmentType | null)[] | null,
  uniqueEventSegmentTypes: EventSegmentType[],
) =>
  types?.map((type) => {
    const legendNumber =
      type === EventSegmentType.Stop || type === EventSegmentType.SelfTransfer || type === null
        ? {}
        : { legendNumber: uniqueEventSegmentTypes.indexOf(type) + 1 };
    return {
      type,
      ...legendNumber,
    };
  });

const generateLegendNumbers = (
  uniqueEventSegmentTypes: EventSegmentType[],
  bound: TravelerDetailsQueryBoundType,
) => {
  if (!bound?.segments) {
    return [];
  }

  const segmentWithLegendNumberArray = bound?.segments?.map((segment) => ({
    ...segment,
    types:
      segment?.__typename === 'EventSegment'
        ? transformEventSegmentTypes(segment.types ?? [], uniqueEventSegmentTypes)
        : [],
  }));

  return segmentWithLegendNumberArray ?? [];
};

export const addLegendNumberToSegmentEvents = (bounds: TravelerDetailsQueryBoundType[]) => {
  const flatEventSegmentTypes = getEventSegmentsList(bounds).flat();
  if (flatEventSegmentTypes.length === 0) {
    return bounds;
  }

  const nonNullFlatEventSegmentTypes = flatEventSegmentTypes.filter(
    (type): type is EventSegmentType => {
      if (type) {
        return true;
      }
      return false;
    },
  );

  const uniqueEventSegmentTypes = Array.from(
    new Set(Array.isArray(nonNullFlatEventSegmentTypes) ? nonNullFlatEventSegmentTypes : []),
  ).filter((type) => type !== EventSegmentType.Stop && type !== EventSegmentType.SelfTransfer);

  return bounds.map((bound) => ({
    ...bound,
    ...{
      segments: generateLegendNumbers(uniqueEventSegmentTypes, bound),
    },
  }));
};

export const populateBoundsWithBaggageInSelectedBundle = (
  bounds: ReturnType<typeof addLegendNumberToSegmentEvents>,
  baggageInBundle: null | { pieces: number; weight: number; weightUnit: string },
) => {
  return bounds.map((bound) => {
    const combinedSegmentsWithBaggage = bound?.segments?.map((segment) => {
      return {
        ...segment,
        includedCheckedBaggage:
          segment && 'includedCheckedBaggage' in segment && segment.includedCheckedBaggage
            ? segment.includedCheckedBaggage
            : baggageInBundle,
      };
    });
    return {
      ...bound,
      segments: combinedSegmentsWithBaggage,
      includedCheckedBaggage:
        bound && 'includedCheckedBaggage' in bound && bound.includedCheckedBaggage
          ? bound.includedCheckedBaggage
          : baggageInBundle,
    };
  });
};

const getOrder = (
  products: ProductInputType[] = [],
  id: number,
  travelers: TransformedTravelersDataType = [],
  bounds: TransformedBounds = [],
): GetOrderReturnType => {
  return products.reduce((acc: GetOrderReturnType, product: ProductInputType) => {
    const productWithoutTypeName = product;
    if ('__typename' in productWithoutTypeName) {
      productWithoutTypeName.__typename = undefined;
    }
    return productWithoutTypeName &&
      'id' in productWithoutTypeName &&
      Number(productWithoutTypeName?.id) === id
      ? { ...acc, ...productWithoutTypeName, bounds, travelers }
      : acc;
  }, {} as GetOrderReturnType);
};

const shouldBeDisplayedPerTraveler = (
  codeName: string,
  productsToDisplayPerTravelerArray: string[],
) => productsToDisplayPerTravelerArray.includes(codeName) || codeName === undefined;

const isTravelInsuranceProduct = (id: number) =>
  [TRAVEL_INSURANCE_INSIDE_EU.id, TRAVEL_INSURANCE_OUTSIDE_EU.id].includes(id as 86 | 87);

export const generateProductComponent =
  (
    travelers: TransformedTravelersDataType,
    bounds: TransformedBounds,
    products: ProductInputType[],
  ) =>
  ({
    component: Component,
    componentPerTraveler,
    id,
    code,
    __typename,
    persuasion,
    isSelectable,
    productIncludedTexts,
    selectedWithTrip,
    sellSpecification,
    shouldShowPricePerBound,
    productPreSelection,
    shouldShowProductsPerTraveler,
    productsToDisplayPerTraveler,
  }: {
    component: ReactNode;
    componentPerTraveler?: ReactNode;
    id: number;
    code: string;
    __typename: string;
    persuasion: { isEnabled: boolean };
    isSelectable?: false;
    productIncludedTexts?: string | ExtraProductTexts | null;
    selectedWithTrip: boolean;
    sellSpecification: AvailableExtraProductsOnTdPageFragmentSellSpecificationType;
    productPreSelection: ProductPreSelection;
    shouldShowPricePerBound: boolean;
    shouldShowProductsPerTraveler: boolean;
    productsToDisplayPerTraveler: string;
  }): GenerateProductComponentReturnFunctionReturnType => {
    const order: GetOrderReturnType = getOrder(products, id, travelers, bounds);
    const orderSecondBag = getOrder(
      products,
      Number(secondBaggagePerTraveler.id),
      travelers,
      bounds,
    );

    const productsToDisplayPerTravelerArray = productsToDisplayPerTraveler?.split(',') ?? [];

    const Comp =
      shouldShowProductsPerTraveler &&
      shouldBeDisplayedPerTraveler(code, productsToDisplayPerTravelerArray) &&
      componentPerTraveler
        ? componentPerTraveler
        : Component;

    return {
      __typename,
      id,
      code,
      component:
        typeof Comp === 'function' ? (
          <Comp
            confirmation={{ isEnabled: isTravelInsuranceProduct(id) }}
            form={TRAVELER_DETAILS}
            isSelectable={isSelectable}
            order={order}
            productIncludedTexts={productIncludedTexts}
            productSpecificTexts={
              'productSpecificTexts' in order ? order?.productSpecificTexts : null
            }
            sellSpecification={sellSpecification}
            shouldShowPricePerBound={shouldShowPricePerBound}
            {...(id === checkInBaggage.id && { orderSecondBag })}
          />
        ) : (
          <></>
        ),
      order,
      persuasion,
      productPreSelection,
      selectedWithTrip,
      sellSpecification,
      shouldShowPricePerBound,
    };
  };
export const transformCountry = (
  country: GetSelectedTripOnTravelerDetailsPageQuery['countries'][number],
) => {
  return {
    id: country.id,
    code: country.code,
    label: country.name,
    phoneCountryPrefix:
      (country.phonePrefixes && country.phonePrefixes[0] && country.phonePrefixes[0]?.prefix) ?? '',
    phoneCountryId:
      (country.phonePrefixes && country.phonePrefixes[0] && country.phonePrefixes[0]?.id) ?? '',
    value: country.name,
  };
};
